import { useStorage } from '@vueuse/core'
import { datadogRum } from '@datadog/browser-rum'
import Preferences from './models/Preferences'

const studioGroups = [
  'studio-admins'
]

const INIT_TTL = 1 * 60 * 1000 // 1 minute

export const useUserStore = defineStore('user', () => {
  const authApi = useAuthApi()
  
  const redirectTo = useStorage('user-redirect-to', null as string | null)
  const getUser = authApi.getUserInfo()
  const prefs = ref(new Preferences())
  const lastInit = ref(0)

  async function init() {
    // cache the user info for a short time to avoid excessive API calls
    const now = Date.now()
    if (now - lastInit.value > INIT_TTL) {
      await loadUser()
      lastInit.value = now
    }
  }

  async function loadPrefs() {
    // Set local prefs
    prefs.value.set(getUser.data.value?.prefs)
    prefs.value.sync()
    // Get the user's loaded collection and initialize the collections store
    await useCollectionsStore().init()
  }

  async function logout() {
    await authApi.logout()  
    getUser.data.value = null
    clearUserFromDatadog()
  }

  function setUserInDatadog() {
    datadogRum.setUser({ id: String(id.value), name: name.value, office: office.value, admin: admin.value })
    datadogRum.setTrackingConsent('granted')
  }

  function clearUserFromDatadog() {
    datadogRum.clearUser()
    datadogRum.setTrackingConsent('not-granted')
  }

  const loadUser = async () => {
    await getUser.execute().then(() => {
      loadPrefs()
      setUserInDatadog()
    })
  }

  // User Info
  const id = computed(() => getUser.data.value?.id)
  const name = computed(() => getUser.data.value?.name)
  const slug = computed(() => getUser.data.value?.slug)
  const email = computed(() => getUser.data.value?.email)
  const roles = computed(() => getUser.data.value?.roles)
  const avatar = computed(() => getUser.data.value?.contact?.profile_image?.medium?.url)
  const office = computed(() => getUser.data.value?.contact?.office || 'Unknown')
  const contact = computed(() => getUser.data.value?.contact)
  // Currently, this will not work because the prefs are loaded after the user is redirected
  const defaultHomePage = computed(() => prefs.value.get('default_home_page') || 'home')

  // Authentication
  const loggedIn = computed(() => getUser.data.value !== null)
  // Authorization
  const groups = computed(() => {
    // Get list of groups
    var unfilteredGroups = getUser.data.value?.groups || []
    // Normalize to array if string
    if (isString(unfilteredGroups)) { unfilteredGroups = [unfilteredGroups] }
    // Remove LDAP prefix and filter by recognized Studio groups
    return unfilteredGroups.map((group: string) => group.match(/cn=([^,]+)/)?.[1] || group).filter((group: string) => studioGroups.includes(group)) || []
  })
  const admin = computed(() => groups.value.includes('studio-admins'))
  const hasRole = (role: string | string[]) => {
    // Admins belong to all roles
    if (admin.value) return true
    // Normalize to array
    if (isString(role)) role = [role]
    // If all roles are allowed, check for login
    if (role.includes('*')) return loggedIn.value
    // Check if user belongs to the role
    return useIntersection(roles.value, role).length > 0 
  }

  return {
    getUser,
    redirectTo,
    init,
    logout,
    loadUser,
    roles,
    prefs,
    id,
    name,
    slug,
    email,
    avatar,
    office,
    contact,
    loggedIn,
    defaultHomePage,
    groups,
    admin,
    hasRole,
  }
})
